/**
 * Utilitários para Web Push / PWA.
 */

/**
 * Converte chave VAPID em formato urlsafe-base64 para Uint8Array aceito pelo PushManager.
 */
export function urlB64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export type PushPlatform = "ios" | "android" | "desktop";

/**
 * Detecta plataforma aproximada via user-agent.
 * Suficiente para orientação de UX (guia de instalação iOS vs. prompt nativo Android).
 */
export function detectPlatform(): PushPlatform {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  // iPadOS 13+ passa a se identificar como Mac com touch — detectar via maxTouchPoints
  const isIPadOS = /Macintosh/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/i.test(ua) || isIPadOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

/**
 * true quando o app está rodando como PWA instalada (display-mode: standalone).
 * No iOS, `navigator.standalone` é truthy quando aberto via Tela de Início.
 */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const standaloneMedia = window.matchMedia?.("(display-mode: standalone)").matches ?? false;
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return standaloneMedia || iosStandalone;
}

/**
 * Verifica se a API de Web Push está disponível no ambiente.
 */
export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * iOS só entrega Web Push se o app estiver instalado como PWA.
 * Retorna `true` se podemos pedir permissão agora.
 */
export function canPromptForPermission(platform: PushPlatform = detectPlatform()): boolean {
  if (!isPushSupported()) return false;
  if (platform === "ios") return isStandalone();
  return true;
}
